import { useState, useEffect } from 'react';
import { Bell, BellOff, LogOut, Loader2, Check, Mail, CalendarDays, MapPin, Plus, Trash2, Pencil, X } from 'lucide-react';
import { memberApi, inputCls } from './memberApi';

const EMPTY_ADDR = { label: '', name: '', street: '', city: '', province: '', postal: '', phone: '' };

const AddressForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || EMPTY_ADDR);
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  const valid = form.name.trim() && form.street.trim() && form.city.trim() && form.postal.trim();
  return (
    <div className="space-y-2.5 bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="address-form">
      <input value={form.label} onChange={set('label')} placeholder="Label (e.g. Home)" maxLength={30} className={inputCls} data-testid="addr-label-input" />
      <input value={form.name} onChange={set('name')} placeholder="Full Name *" className={inputCls} data-testid="addr-name-input" />
      <input value={form.street} onChange={set('street')} placeholder="Street Address *" className={inputCls} data-testid="addr-street-input" />
      <div className="grid grid-cols-3 gap-2">
        <input value={form.city} onChange={set('city')} placeholder="City *" className={inputCls} data-testid="addr-city-input" />
        <input value={form.province} onChange={set('province')} placeholder="Province" className={inputCls} data-testid="addr-province-input" />
        <input value={form.postal} onChange={set('postal')} placeholder="Postal *" className={inputCls} data-testid="addr-postal-input" />
      </div>
      <input value={form.phone} onChange={set('phone')} placeholder="Phone" className={inputCls} data-testid="addr-phone-input" />
      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave(form)} disabled={!valid || saving} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors" data-testid="addr-save-btn">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save Address
        </button>
        <button onClick={onCancel} className="flex items-center gap-1.5 border border-[#27272A] text-gray-400 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors" data-testid="addr-cancel-btn">
          <X size={13} /> Cancel
        </button>
      </div>
    </div>
  );
};

export const ProfileTab = ({ user, logout, refresh }) => {
  const [profile, setProfile] = useState(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [phone, setPhone] = useState('');
  const [reminders, setReminders] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [addrMode, setAddrMode] = useState(null); // 'new' | addr_id
  const [addrSaving, setAddrSaving] = useState(false);

  const loadProfile = () => {
    memberApi.get('/member/profile').then(r => {
      setProfile(r.data);
      setFirst(r.data.first_name || '');
      setLast(r.data.last_name || '');
      setPhone(r.data.phone || '');
      setReminders(r.data.reminder_emails !== false);
    }).catch(() => setProfile({ email: user.email, addresses: [] }));
  };

  useEffect(loadProfile, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    setSaving(true);
    try {
      await memberApi.patch('/member/settings', { first_name: first, last_name: last, phone });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      refresh();
    } catch {} finally { setSaving(false); }
  };

  const toggleReminders = () => {
    const next = !reminders;
    setReminders(next);
    memberApi.patch('/member/settings', { reminder_emails: next }).catch(() => setReminders(!next));
  };

  const saveAddress = async (form) => {
    setAddrSaving(true);
    try {
      if (addrMode === 'new') await memberApi.post('/member/addresses', form);
      else await memberApi.patch(`/member/addresses/${addrMode}`, form);
      setAddrMode(null);
      loadProfile();
    } catch {} finally { setAddrSaving(false); }
  };

  const deleteAddress = async (id) => {
    await memberApi.delete(`/member/addresses/${id}`).catch(() => {});
    loadProfile();
  };

  if (!profile) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#C41E3A]" size={28} /></div>;
  }

  const initial = (first || user.email)[0].toUpperCase();
  const memberSince = profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
  const addresses = profile.addresses || [];

  return (
    <div className="space-y-4" data-testid="profile-tab">
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#C41E3A]/15 border-2 border-[#C41E3A] flex items-center justify-center text-[#C41E3A] font-['Oswald'] text-2xl flex-shrink-0" data-testid="profile-avatar">{initial}</div>
        <div className="min-w-0">
          <p className="text-white font-['Oswald'] text-lg uppercase tracking-wide truncate">{[first, last].filter(Boolean).join(' ') || user.email.split('@')[0]}</p>
          <p className="flex items-center gap-1.5 text-gray-400 text-xs truncate"><Mail size={11} /> {user.email}</p>
          {memberSince && <p className="flex items-center gap-1.5 text-[#D4AF37] text-xs mt-1"><CalendarDays size={11} /> Member since {memberSince}</p>}
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3">Profile Details</h3>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <input value={first} onChange={e => setFirst(e.target.value)} maxLength={40} placeholder="First name" className={inputCls} data-testid="profile-first-input" />
            <input value={last} onChange={e => setLast(e.target.value)} maxLength={40} placeholder="Last name" className={inputCls} data-testid="profile-last-input" />
          </div>
          <input value={phone} onChange={e => setPhone(e.target.value)} maxLength={25} placeholder="Phone number" type="tel" className={inputCls} data-testid="profile-phone-input" />
          <button onClick={saveProfile} disabled={saving} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors" data-testid="profile-save-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : null} {saved ? 'Saved' : 'Save Profile'}
          </button>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5" data-testid="saved-addresses-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37]">Saved Addresses</h3>
          {addrMode !== 'new' && addresses.length < 5 && (
            <button onClick={() => setAddrMode('new')} className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 hover:text-white border border-[#27272A] hover:border-[#C41E3A] px-3 py-1.5 rounded-sm transition-colors" data-testid="add-address-btn">
              <Plus size={12} /> Add
            </button>
          )}
        </div>
        <p className="text-gray-500 text-xs mb-3">Saved addresses can be used to fill your checkout in one tap.</p>

        {addrMode === 'new' && <div className="mb-3"><AddressForm onSave={saveAddress} onCancel={() => setAddrMode(null)} saving={addrSaving} /></div>}

        {addresses.length === 0 && addrMode !== 'new' && (
          <p className="text-gray-600 text-sm" data-testid="no-addresses">No saved addresses yet.</p>
        )}

        <div className="space-y-2">
          {addresses.map(a => addrMode === a.addr_id ? (
            <AddressForm key={a.addr_id} initial={a} onSave={saveAddress} onCancel={() => setAddrMode(null)} saving={addrSaving} />
          ) : (
            <div key={a.addr_id} className="flex items-start justify-between gap-3 bg-[#121212] border border-[#27272A] rounded-sm p-3.5" data-testid={`address-card-${a.addr_id}`}>
              <div className="flex items-start gap-2.5 min-w-0">
                <MapPin size={14} className="text-[#C41E3A] mt-0.5 flex-shrink-0" />
                <div className="min-w-0 text-sm">
                  <p className="text-white font-semibold truncate">{a.label || a.name}</p>
                  <p className="text-gray-400 text-xs truncate">{a.name} · {a.street}</p>
                  <p className="text-gray-500 text-xs">{[a.city, a.province, a.postal].filter(Boolean).join(', ')}</p>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => setAddrMode(a.addr_id)} className="p-2 text-gray-500 hover:text-white transition-colors" data-testid={`edit-address-${a.addr_id}`}><Pencil size={13} /></button>
                <button onClick={() => deleteAddress(a.addr_id)} className="p-2 text-gray-500 hover:text-[#C41E3A] transition-colors" data-testid={`delete-address-${a.addr_id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3">Email Reminders</h3>
        <p className="text-gray-400 text-sm mb-3">Get an evening email if you haven't logged a scheduled injection.</p>
        <button
          onClick={toggleReminders}
          className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${reminders ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#27272A] text-gray-500 hover:text-gray-300'}`}
          data-testid="reminder-toggle-btn"
        >
          {reminders ? <Bell size={12} /> : <BellOff size={12} />} {reminders ? 'Reminders On' : 'Reminders Off'}
        </button>
      </div>

      <button onClick={logout} className="w-full flex items-center justify-center gap-2 border border-[#27272A] hover:border-[#C41E3A] text-gray-400 hover:text-white text-xs font-bold uppercase tracking-wider py-3 rounded-sm transition-colors" data-testid="profile-signout-btn">
        <LogOut size={13} /> Sign Out
      </button>
    </div>
  );
};
